import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ellipse1Svg, ellipse2Svg, ellipse3Svg, leftArrowSvg, rightArrowSvg } from '../assets/svg';
import { dateFormat, formattedDate, getDatesInMonth } from '../utils/utilities';
import '../css/calendar.css';
import { useDispatch, useSelector } from 'react-redux';
import { updateDate } from '../Redux/Reducers/appointmentFilterSlice';

const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

function Calendar({ onDateChange, onMonthChange }) {
    const selectedDateISOString = useSelector(
        (state) => state.appointment.selectedDate
    );
    const selectedDate = useMemo(
        () => new Date(selectedDateISOString),
        [selectedDateISOString]
    );

    const [activeDate, setActiveDate] = useState(selectedDate);
    const [month, setMonth] = useState(getDatesInMonth(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1
    ));

    const dispatch = useDispatch();

    useEffect(() => {
        setActiveDate(selectedDate);
        setMonth(getDatesInMonth(
            selectedDate.getFullYear(),
            selectedDate.getMonth() + 1
        ));
    }, [selectedDate]);

    const handleDateClick = useCallback((date) => {
        onDateChange(new Date(date));
    }, [onDateChange]);

    const handleMonthChange = useCallback((increment) => {
        const newDate = new Date(activeDate);
        newDate.setMonth(activeDate.getMonth() + increment);
        setActiveDate(newDate);
        setMonth(getDatesInMonth(newDate.getFullYear(), newDate.getMonth() + 1));
        onDateChange(newDate);  // Notify the parent component of the date change
        onMonthChange();  // Keep the calendar open
    }, [activeDate, onDateChange, onMonthChange]);

    const renderHeader = useMemo(() => (
        <div className="header my-2">
            <div className="header-content d-flex justify-content-evenly mb-4">
                <div className="arrow" onClick={() => handleMonthChange(-1)}>
                    {leftArrowSvg({ size: 30, color: "white" })}
                </div>
                <div className="date">
                    {dateFormat(activeDate).month + ", " + dateFormat(activeDate).year}
                </div>
                <div className="arrow" onClick={() => handleMonthChange(1)}>
                    {rightArrowSvg({ size: 30, color: "white" })}
                </div>
            </div>
            <div className="days-of-week d-flex">
                {daysOfWeek.map((day, index) => (
                    <div key={index} className="day col text-center">{day.slice(0, 2)}</div>
                ))}
            </div>
        </div>
    ), [activeDate, handleMonthChange]);

    const renderBody = useMemo(() => {
        const rows = [];
        let currentDateIndex = 0;
        while (currentDateIndex < month?.length) {
            const row = [];
            for (const weekDay of daysOfWeek) {
                if (
                    currentDateIndex < month.length &&
                    month[currentDateIndex].day === weekDay
                ) {
                    let date = new Date(month[currentDateIndex].date).getDate();
                    const currentSlotIndex = currentDateIndex;

                    row.push(
                        <div
                            key={weekDay + currentSlotIndex}
                            className={`calendar-cell ${formattedDate(activeDate) === formattedDate(new Date(month[currentSlotIndex]?.date)) ? 'active-date' : ''}`}
                            onClick={() => handleDateClick(new Date(month[currentSlotIndex].date))}
                        >
                            <div style={{ fontWeight: 400, fontSize: "16px" }}>{date}</div>
                        </div>
                    );
                    currentDateIndex++;
                } else {
                    row.push(<div key={weekDay + currentDateIndex} className="col text-center"
                        style={{
                            width: "40px",
                            backgroundColor: 'transparent',
                        }}></div>);
                }
            }
            rows.push(<div key={currentDateIndex} className="d-flex justify-content-between week-bg-none py-1">{row}</div>);
        }

        return <div className="calendar-body">{rows}</div>;
    }, [month, handleDateClick, activeDate]);

    return (
        <div className="calendar-container p-3">
            <span style={{ position: "absolute", top: "3%", left: 0 }}>
                {ellipse1Svg({})}
            </span>
            <span style={{ position: "absolute", top: "6%", right: 0 }}>
                {ellipse2Svg({})}
            </span>
            <span style={{ position: "absolute", bottom: 0, left: 0 }}>
                {ellipse3Svg({})}
            </span>
            {renderHeader}
            {renderBody}
        </div>
    );
}

export default React.memo(Calendar);
